import React, { useEffect, useContext } from 'react';
import { CanonicalContext } from './LanguageRouter';

// Base URL for the site
const SITE_URL = 'https://fxastra.com';
const SITE_NAME = 'FX Astra';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

// Organization Schema (used site-wide)
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://fxastra.com/#organization",
  "name": "FX Astra",
  "alternateName": "FXAstra",
  "url": SITE_URL,
  "logo": `${SITE_URL}/logo.png`,
  "description": "The ultimate forex broker comparison and trading platform. Compare 5,000+ brokers, access live charts, economic calendar, and trading tools.",
  "foundingDate": "2024",
  "founder": {
    "@type": "Person",
    "name": "Amiran Azaladze",
    "jobTitle": "Founder & CEO",
    "url": `${SITE_URL}/amiran-azaladze`
  },
  "sameAs": [
    "https://twitter.com/fxastra",
    "https://linkedin.com/company/fxastra",
    "https://facebook.com/fxastra"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+44-7916-620316",
    "contactType": "customer service",
    "email": "support@fxastra.com",
    "areaServed": "Worldwide",
    "availableLanguage": ["English", "Arabic", "Chinese", "Spanish"]
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2475 Hanover Street, Suite 200",
    "addressLocality": "Palo Alto",
    "addressRegion": "CA",
    "postalCode": "94304",
    "addressCountry": "US"
  }
};

// Website Schema with SearchAction
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "FX Astra",
  "url": SITE_URL,
  "description": "Compare forex brokers, access live charts, economic calendar, and trading tools.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/brokers?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

// SEO Component for general pages.
// React 19 hoists <meta>/<link> rendered anywhere in the tree into <head>, so no Helmet is needed.
// Canonical + hreflang links are emitted by LanguageRouter for every routed page.
export function SEO({ 
  title, 
  description, 
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  forceCanonical = false,
  schemas = [],
  children 
}) {
  const fullTitle = !title ? `${SITE_NAME} - Forex Broker Comparison & Trading Tools` : title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const fullDescription = description || 'Compare 5,000+ forex brokers, access live trading charts, economic calendar, market news, and professional trading tools. Find the best broker for your trading needs.';
  const fullCanonical = canonicalUrl ? `${SITE_URL}${canonicalUrl}` : undefined;
  const allSchemas = [organizationSchema, websiteSchema, ...schemas];

  useEffect(() => { document.title = fullTitle; }, [fullTitle]);
  const setCanonical = useContext(CanonicalContext);
  useEffect(() => { if (forceCanonical && fullCanonical) setCanonical(fullCanonical); }, [forceCanonical, fullCanonical, setCanonical]);

  return (
    <>
      <meta name="description" content={fullDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}

      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={ogImage} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(allSchemas) }} />

      {children}
    </>
  );
}

// Broker page URL helper (name-based slug, UUID fallback)
export const brokerPath = (b) => `/brokers/${b?.slug || b?.id}`;

const absUrl = (u) => (!u ? undefined : u.startsWith('http') ? u : `${SITE_URL}${u.startsWith('/') ? '' : '/'}${u}`);

// Broker/Company Schema Generator — FinancialService entity for the broker page
export function generateBrokerSchema(broker, reviews = []) {
  const pageUrl = `${SITE_URL}${brokerPath(broker)}`;
  const regs = (broker.regulations || []).filter(r => r && r !== 'Unregulated');
  const ratingCount = reviews.length || broker.total_reviews || 0;
  const avg = reviews.length
    ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length
    : Number(broker.user_rating) || 0;
  return {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "@id": `${pageUrl}#broker`,
    "name": broker.name,
    "url": broker.website || pageUrl,
    "mainEntityOfPage": pageUrl,
    "description": broker.description,
    "logo": absUrl(broker.logo_url),
    "image": absUrl(broker.logo_url) || DEFAULT_IMAGE,
    "foundingDate": broker.founded_year ? String(broker.founded_year) : undefined,
    "address": broker.country ? { "@type": "PostalAddress", "addressCountry": broker.country } : undefined,
    "areaServed": "Worldwide",
    "serviceType": ["Forex trading", "CFD trading"],
    "knowsAbout": [...regs, ...(broker.platforms || [])],
    "sameAs": broker.website ? [broker.website] : undefined,
    "brand": { "@type": "Brand", "name": broker.name },
    "aggregateRating": ratingCount > 0 && avg > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": Number(avg.toFixed(1)),
      "bestRating": 5,
      "worstRating": 1,
      "ratingCount": ratingCount
    } : undefined,
    "review": !reviews.length ? undefined : reviews.slice(0, 5).map(r => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.user_name || "FX Astra user" },
      "datePublished": (r.created_at || '').slice(0, 10) || undefined,
      "name": r.title,
      "reviewBody": r.content,
      "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5, "worstRating": 1 }
    })),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${broker.name} trading accounts`,
      "itemListElement": (broker.account_types?.length ? broker.account_types : ['Standard']).map(a => ({
        "@type": "Offer",
        "itemOffered": { "@type": "Service", "name": `${a} account`, "provider": { "@id": `${pageUrl}#broker` } },
        "priceSpecification": broker.min_deposit != null ? { "@type": "PriceSpecification", "price": broker.min_deposit, "priceCurrency": "USD", "description": "Minimum deposit" } : undefined
      }))
    }
  };
}

// Data-driven FAQ for a broker (rendered on page AND emitted as FAQPage schema)
export function generateBrokerFaq(broker) {
  const regs = (broker.regulations || []).filter(r => r && r !== 'Unregulated');
  const fmt = (n) => (Number(n) === 0 ? 'no minimum deposit' : `$${Number(n).toLocaleString()}`);
  return [
    { q: `Is ${broker.name} regulated?`, a: regs.length
      ? `Yes. ${broker.name} holds ${regs.length} licence${regs.length > 1 ? 's' : ''}: ${regs.join(', ')}. Always verify a licence number directly on the regulator's public register before depositing.`
      : `FX Astra has no record of an active licence for ${broker.name}. Trading with an unregulated broker means no investor-compensation scheme or supervisory oversight — proceed with caution.` },
    { q: `What is the minimum deposit at ${broker.name}?`, a: `${broker.name} requires ${fmt(broker.min_deposit)} to open a live account${broker.account_types?.length ? ` (account types: ${broker.account_types.join(', ')})` : ''}.` },
    { q: `What leverage and spreads does ${broker.name} offer?`, a: `Maximum leverage is ${broker.max_leverage || 'not disclosed'} and spreads start from ${broker.spreads_from ?? 'n/a'} pips on major currency pairs. Leverage may be capped lower by your local regulator.` },
    { q: `Which trading platforms does ${broker.name} support?`, a: broker.platforms?.length ? `${broker.name} offers ${broker.platforms.join(', ')} across desktop, web and mobile.` : `${broker.name} has not published its platform line-up.` },
    { q: `How can I deposit and withdraw at ${broker.name}?`, a: `${broker.deposit_methods?.length ? `Deposits: ${broker.deposit_methods.join(', ')}. ` : ''}${broker.withdrawal_methods?.length ? `Withdrawals: ${broker.withdrawal_methods.join(', ')}. ` : ''}Customer support is available ${broker.support_hours || '24/5'}.` },
    { q: `Where is ${broker.name} based and when was it founded?`, a: `${broker.name} is headquartered in ${broker.country || 'an undisclosed jurisdiction'}${broker.founded_year ? ` and was founded in ${broker.founded_year}` : ''}.` },
  ];
}

export function generateFaqSchema(faq) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(({ q, a }) => ({ "@type": "Question", "name": q, "acceptedAnswer": { "@type": "Answer", "text": a } }))
  };
}

// Unique, data-driven "About" copy so every broker page has distinct body text
export function generateBrokerAbout(broker) {
  const regs = (broker.regulations || []).filter(r => r && r !== 'Unregulated');
  const yrs = broker.founded_year ? new Date().getFullYear() - Number(broker.founded_year) : null;
  const p1 = `${broker.name} is a ${broker.country ? `${broker.country}-based ` : ''}forex and CFD broker${broker.founded_year ? ` founded in ${broker.founded_year}` : ''}${yrs && yrs > 0 ? ` (${yrs} year${yrs > 1 ? 's' : ''} in operation)` : ''}. ${regs.length ? `It is regulated by ${regs.join(', ')}, which places it under formal supervisory oversight.` : 'FX Astra has no record of regulatory authorisation for this broker, which is a key risk factor to weigh.'}`;
  const p2 = `Live accounts open from ${Number(broker.min_deposit) === 0 ? 'no minimum deposit' : `$${Number(broker.min_deposit).toLocaleString()}`}, with leverage up to ${broker.max_leverage || 'undisclosed'} and spreads from ${broker.spreads_from ?? 'n/a'} pips.${broker.account_types?.length ? ` Account options include ${broker.account_types.join(', ')}.` : ''}${broker.platforms?.length ? ` Trading is available on ${broker.platforms.join(', ')}.` : ''}`;
  const p3 = `${broker.deposit_methods?.length ? `Funding methods include ${broker.deposit_methods.join(', ')}` : 'Funding methods are not published'}${broker.withdrawal_methods?.length ? `; withdrawals via ${broker.withdrawal_methods.join(', ')}` : ''}. Support runs ${broker.support_hours || '24/5'}. ${broker.name} ranks #${broker.rank || '—'} of 5,000+ brokers in the FX Astra index with a trust score of ${broker.trust_score ?? '—'}/10.`;
  return [p1, p2, p3];
}

// Review Schema Generator
export function generateReviewSchema(review, broker) {
  return {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "FinancialService",
      "name": broker.name
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": review.rating,
      "bestRating": 5,
      "worstRating": 1
    },
    "author": {
      "@type": "Person",
      "name": review.author_name || "Anonymous"
    },
    "datePublished": review.created_at,
    "reviewBody": review.content
  };
}

// Article/News Schema Generator
export function generateArticleSchema(article) {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.summary || article.excerpt,
    "image": article.featured_image_url || article.image_url || DEFAULT_IMAGE,
    "datePublished": article.published_at || article.created_at,
    "dateModified": article.updated_at || article.published_at || article.created_at,
    "author": {
      "@type": "Person",
      "name": article.author_name || "FX Astra Editorial"
    },
    "publisher": {
      "@type": "Organization",
      "name": "FX Astra",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/news/article/${article.slug || article.id}`
    }
  };
}

// Job Posting Schema Generator
export function generateJobPostingSchema(job) {
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "datePosted": job.posted_date,
    "validThrough": job.valid_through || "2025-12-31",
    "employmentType": job.type === 'Full-time' ? "FULL_TIME" : "PART_TIME",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "FX Astra",
      "sameAs": SITE_URL,
      "logo": `${SITE_URL}/logo.png`
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location?.includes("Dubai") ? "Dubai" : "Remote",
        "addressCountry": "UAE"
      }
    },
    "baseSalary": job.salary ? {
      "@type": "MonetaryAmount",
      "currency": job.salary.currency || "USD",
      "value": {
        "@type": "QuantitativeValue",
        "value": job.salary.base,
        "unitText": job.salary.period === "month" ? "MONTH" : "YEAR"
      }
    } : undefined,
    "jobBenefits": job.benefits?.join(", "),
    "skills": job.requirements?.join(", "),
    "responsibilities": job.responsibilities?.join(". ")
  };
}

// Breadcrumb Schema Generator
export function generateBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `${SITE_URL}${item.url}` : undefined
    }))
  };
}

// FAQ Schema Generator
export function generateFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

// Event Schema (for awards, webinars, etc.)
export function generateEventSchema(event) {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.name,
    "description": event.description,
    "startDate": event.start_date,
    "endDate": event.end_date,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "location": {
      "@type": "VirtualLocation",
      "url": `${SITE_URL}/awards`
    },
    "organizer": {
      "@type": "Organization",
      "name": "FX Astra",
      "url": SITE_URL
    }
  };
}

export default SEO;
